import PropTypes from 'prop-types'
import React from 'react'
import { ValidationFunction } from './Validations'

export interface FormCollectorState {
  formData: FormData
  isValid: boolean
}

export interface FormContextMethods {
  onFormElementChange?: (key: string, value: string, isValid: boolean) => void
}

export interface FormData {
  [name: string]: string
}

export interface FormErrors {
  [name: string]: string
}

export const flattenErrors = (serverErrors): FormErrors => {
  const errors: FormErrors = {}
  serverErrors.forEach(error => {
    if (error.fields) {
      Object.keys(error.fields).forEach(key => {
        const value = error.fields[key].join(', ')
        errors[key] = value
      })
    } else {
      // TODO: better handle gQL errors?
      errors.message = error.message
    }
  })
  return errors
}

export const formatFormDataForServer = (
  formData: FormData | undefined,
): any => {
  if (formData === undefined) {
    return null
  }
  return Object.keys(formData).reduce((accum, key) => {
    const keys = key.split('.')
    const value = formData[key]
    let newValue: string | boolean = value
    if (newValue === 'true') {
      newValue = true
    } else if (newValue === 'false') {
      newValue = false
    }
    const primaryKey = keys[0]
    if (keys.length > 1) {
      const secondaryKey = keys[1]
      accum[primaryKey] = accum[primaryKey] || {}
      accum[primaryKey][secondaryKey] = newValue
    } else {
      accum[primaryKey] = newValue
    }
    return accum
  }, {})
}

export const isValidWhenNullOrFalse = (
  isRequired: boolean | undefined,
  value: string | null | boolean,
): boolean => {
  return (
    (!isRequired && value !== null && value === '') ||
    (!isRequired && value === null) ||
    (!isRequired && value === undefined) ||
    (!isRequired && value === false)
  )
}

export const withFormCollector = WrappedComponent => {
  return class extends React.Component<{}, FormCollectorState> {
    static childContextTypes = {
      onFormElementChange: PropTypes.func,
    }

    formData = {}
    formValidity = {}

    constructor(props) {
      super(props)
      this.state = { formData: this.formData, isValid: false }
    }

    getChildContext() {
      return {
        onFormElementChange: this.onFormElementChange,
      }
    }

    isFormValid = () => {
      return Object.keys(this.formData).every(key => {
        let isValid = true
        const value = this.formData[key]
        const validations = this.formValidity[key].validations
        const isRequired = this.formValidity[key].isRequired
        if (isValidWhenNullOrFalse(isRequired, value)) {
          isValid = true
        } else if (validations && validations.length) {
          isValid = validations.every(v => v(value))
        }
        return isValid
      })
    }

    onFormElementChange = (
      key: string,
      value: string,
      isRequired: boolean,
      validations?: ValidationFunction[],
    ) => {
      this.formData[key] = value
      this.formValidity[key] = {
        isRequired,
        validations,
      }
      this.setState({ formData: this.formData, isValid: this.isFormValid() })
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          formData={this.state.formData}
          isValid={this.state.isValid}
          onFormElementChange={this.onFormElementChange}
        />
      )
    }
  }
}
